'use client';
import React, { useState } from 'react';
import { Shield, FileText, Eye, CheckCircle, XCircle, AlertTriangle, Users, Flag, Lock, ChevronRight, ChevronDown, Download, RefreshCw, Clock, Camera, UserCheck, Building2, ArrowRight, Info, Zap, ThumbsUp, ThumbsDown, BarChart3, Send, ChevronLeft } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionHeader from '@/components/ui/SectionHeader';
import MetricCard from '@/components/ui/MetricCard';

// ─── Types ────────────────────────────────────────────────────────────────────
type RiskTier = 'low' | 'medium' | 'high' | 'critical';
type DocStatus = 'pending' | 'verified' | 'rejected' | 'under-review';
type LivenessStatus = 'not-started' | 'passed' | 'failed' | 'manual-override';
type UBODecision = 'pending' | 'approved' | 'rejected';
type WorkflowDecision = 'eligible' | 'hold' | null;

interface ApplicantDoc {
  id: string;
  name: string;
  type: string;
  uploadedAt: string;
  size: string;
  status: DocStatus;
  issuer?: string;
  expiry?: string;
  notes?: string;
}

interface UBOEntry {
  id: string;
  name: string;
  nationality: string;
  ownership: number;
  isPEP: boolean;
  sanctionHit: boolean;
  docStatus: 'pending' | 'uploaded' | 'verified';
  decision: UBODecision;
  rejectionReason?: string;
}

interface AuditEvent {
  ts: string;
  actor: string;
  action: string;
  detail: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const APPLICANT = {
  id: 'KYC-2026-00441',
  name: 'Thornfield Capital Holdings Ltd',
  type: 'Corporate (KYB)',
  jurisdiction: 'Cayman Islands',
  submittedAt: '2026-08-18 09:14 UTC',
  assignee: 'L. Okafor',
  queue: 'Enhanced Due Diligence (EDD)',
  intakeRef: 'INTAKE-2026-0882',
  email: 'compliance@thornfieldcapital.ky',
  incorporationNo: 'CI-2019-88421',
  fatcaStatus: 'Passive NFFE',
  pepDeclared: true,
};

const INITIAL_DOCS: ApplicantDoc[] = [
  { id: 'doc-1', name: 'Certificate_of_Incorporation.pdf', type: 'Certificate of Incorporation', uploadedAt: '2026-08-18 09:14', size: '1.2 MB', status: 'verified', issuer: 'Cayman Islands Registrar', expiry: 'N/A' },
  { id: 'doc-2', name: 'Memorandum_Articles.pdf', type: 'Memorandum & Articles of Association', uploadedAt: '2026-08-18 09:15', size: '3.8 MB', status: 'verified', issuer: 'Thornfield Capital Holdings Ltd', expiry: 'N/A' },
  { id: 'doc-3', name: 'Register_of_Directors.pdf', type: 'Register of Directors', uploadedAt: '2026-08-18 09:16', size: '0.9 MB', status: 'under-review', issuer: 'Thornfield Capital Holdings Ltd', expiry: 'N/A' },
  { id: 'doc-4', name: 'Proof_of_Address_HQ.pdf', type: 'Proof of Registered Address', uploadedAt: '2026-08-18 09:17', size: '0.5 MB', status: 'verified', issuer: 'Cayman National Bank', expiry: '2026-09-01' },
  { id: 'doc-5', name: 'UBO_Passport_Volkov.pdf', type: 'UBO Passport — Dmitri Volkov', uploadedAt: '2026-08-18 09:20', size: '2.1 MB', status: 'rejected', issuer: 'Russian Federation', expiry: '2027-03-14', notes: 'Possible OFAC SDN partial match — requires manual review' },
  { id: 'doc-6', name: 'Source_of_Funds_Declaration.pdf', type: 'Source of Funds Declaration', uploadedAt: '2026-08-18 09:22', size: '0.7 MB', status: 'under-review', issuer: 'Thornfield Capital Holdings Ltd', expiry: 'N/A' },
];

const INITIAL_UBOS: UBOEntry[] = [
  { id: 'ubo-1', name: 'Dmitri Volkov', nationality: 'Russian Federation', ownership: 62, isPEP: false, sanctionHit: true, docStatus: 'verified', decision: 'pending' },
  { id: 'ubo-2', name: 'Amelia Hartmann', nationality: 'Germany', ownership: 28, isPEP: true, sanctionHit: false, docStatus: 'verified', decision: 'pending' },
  { id: 'ubo-3', name: 'Thornfield Trust (Nominee)', nationality: 'Cayman Islands', ownership: 10, isPEP: false, sanctionHit: false, docStatus: 'pending', decision: 'pending' },
];

const INITIAL_AUDIT: AuditEvent[] = [
  { ts: '2026-08-18 09:14', actor: 'System', action: 'Case Created', detail: 'KYC/KYB intake submitted via portal. Assigned to EDD queue.' },
  { ts: '2026-08-18 10:02', actor: 'System', action: 'Liveness Check Initiated', detail: 'eKYC session launched for primary contact. Session ID: LV-882-4491.' },
  { ts: '2026-08-18 10:09', actor: 'System', action: 'Liveness Check Passed', detail: 'Biometric match confidence: 98.4%. No spoof detected.' },
  { ts: '2026-08-19 11:30', actor: 'L. Okafor', action: 'Sanctions Screening', detail: 'OFAC SDN partial match flagged for Dmitri Volkov (UBO-1). Score: 87/100.' },
  { ts: '2026-08-20 08:45', actor: 'L. Okafor', action: 'Document Review Started', detail: 'Officer commenced document verification for all 6 uploaded files.' },
];

const RISK_TIERS: { tier: RiskTier; label: string; description: string; color: string }[] = [
  { tier: 'low', label: 'Low Risk', description: 'Standard onboarding. No enhanced measures required.', color: 'text-positive' },
  { tier: 'medium', label: 'Medium Risk', description: 'Periodic enhanced monitoring. Annual re-KYC.', color: 'text-warning' },
  { tier: 'high', label: 'High Risk', description: 'EDD required. 6-month re-KYC cycle. Senior approval.', color: 'text-negative' },
  { tier: 'critical', label: 'Critical / Prohibited', description: 'Onboarding blocked. Escalate to MLRO immediately.', color: 'text-critical' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function DocStatusBadge({ status }: { status: DocStatus }) {
  const map: Record<DocStatus, { variant: 'active' | 'pending' | 'review' | 'rejected'; label: string }> = {
    verified: { variant: 'active', label: 'Verified' },
    pending: { variant: 'pending', label: 'Pending' },
    'under-review': { variant: 'review', label: 'Under Review' },
    rejected: { variant: 'rejected', label: 'Rejected' },
  };
  return <StatusBadge variant={map[status].variant} label={map[status].label} dot />;
}

function DocReviewRow({
  doc,
  onStatusChange,
  onNoteChange,
}: {
  doc: ApplicantDoc;
  onStatusChange: (id: string, status: DocStatus) => void;
  onNoteChange: (id: string, note: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded-lg border transition-colors ${doc.status === 'rejected' ? 'border-negative/40 bg-negative/5' : doc.status === 'verified' ? 'border-positive/30 bg-positive/5' : 'border-border bg-surface-3'}`}>
      <div
        className="flex items-center gap-3 p-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="w-8 h-8 rounded-md bg-surface-4 flex items-center justify-center flex-shrink-0">
          <FileText size={14} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{doc.name}</p>
          <p className="text-xs text-muted-foreground">{doc.type}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs font-mono-data text-muted-foreground hidden sm:block">{doc.uploadedAt}</span>
          <DocStatusBadge status={doc.status} />
          <ChevronDown size={14} className={`text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-border pt-3 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div><p className="text-muted-foreground mb-0.5">Issuer</p><p className="text-foreground font-medium">{doc.issuer || '—'}</p></div>
            <div><p className="text-muted-foreground mb-0.5">Expiry</p><p className="text-foreground font-mono-data">{doc.expiry || '—'}</p></div>
            <div><p className="text-muted-foreground mb-0.5">File Size</p><p className="text-foreground font-mono-data">{doc.size}</p></div>
            <div><p className="text-muted-foreground mb-0.5">Uploaded</p><p className="text-foreground font-mono-data">{doc.uploadedAt}</p></div>
          </div>

          {doc.notes && (
            <div className="flex items-start gap-2 p-2 rounded-md bg-warning/10 border border-warning/30">
              <AlertTriangle size={13} className="text-warning mt-0.5 flex-shrink-0" />
              <p className="text-xs text-warning">{doc.notes}</p>
            </div>
          )}

          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground font-medium">Officer Note</p>
            <textarea
              rows={2}
              placeholder="Add review note for this document…"
              defaultValue={doc.notes || ''}
              onChange={(e) => onNoteChange(doc.id, e.target.value)}
              className="w-full bg-surface-4 border border-border rounded-md px-3 py-2 text-xs text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:border-primary"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onStatusChange(doc.id, 'verified')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${doc.status === 'verified' ? 'bg-positive/20 text-positive border border-positive/40' : 'bg-surface-4 text-muted-foreground border border-border hover:border-positive/40 hover:text-positive'}`}
            >
              <CheckCircle size={12} /> Verify
            </button>
            <button
              onClick={() => onStatusChange(doc.id, 'rejected')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${doc.status === 'rejected' ? 'bg-negative/20 text-negative border border-negative/40' : 'bg-surface-4 text-muted-foreground border border-border hover:border-negative/40 hover:text-negative'}`}
            >
              <XCircle size={12} /> Reject
            </button>
            <button
              onClick={() => onStatusChange(doc.id, 'under-review')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${doc.status === 'under-review' ? 'bg-warning/20 text-warning border border-warning/40' : 'bg-surface-4 text-muted-foreground border border-border hover:border-warning/40 hover:text-warning'}`}
            >
              <Eye size={12} /> Flag for Review
            </button>
            <button className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-surface-4 text-muted-foreground border border-border hover:text-foreground transition-colors">
              <Download size={12} /> Download
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function UBOReviewCard({
  ubo,
  onDecision,
}: {
  ubo: UBOEntry;
  onDecision: (id: string, decision: UBODecision, reason?: string) => void;
}) {
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  return (
    <div className={`rounded-xl border p-4 space-y-3 transition-colors ${ubo.decision === 'approved' ? 'border-positive/40 bg-positive/5' : ubo.decision === 'rejected' ? 'border-negative/40 bg-negative/5' : 'border-border bg-surface-3'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-surface-4 flex items-center justify-center flex-shrink-0">
            <Users size={16} className="text-primary" />
          </div>
          <div>
            <p className="font-semibold text-sm text-foreground">{ubo.name}</p>
            <p className="text-xs text-muted-foreground">{ubo.nationality}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {ubo.decision === 'approved' && <StatusBadge variant="active" label="Approved" dot />}
          {ubo.decision === 'rejected' && <StatusBadge variant="rejected" label="Rejected" dot />}
          {ubo.decision === 'pending' && <StatusBadge variant="pending" label="Pending" dot />}
        </div>
      </div>

      {/* Ownership bar */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-muted-foreground">Ownership Stake</p>
          <p className="text-xs font-mono-data font-bold text-foreground">{ubo.ownership}%</p>
        </div>
        <div className="h-2 rounded-full bg-surface-4 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${ubo.ownership >= 50 ? 'bg-negative' : ubo.ownership >= 25 ? 'bg-warning' : 'bg-positive'}`}
            style={{ width: `${ubo.ownership}%` }}
          />
        </div>
        {ubo.ownership >= 25 && (
          <p className="text-xs text-warning mt-1 flex items-center gap-1">
            <AlertTriangle size={10} /> Reportable UBO — exceeds 25% threshold
          </p>
        )}
      </div>

      {/* Flags */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${ubo.isPEP ? 'bg-warning/15 text-warning border-warning/30' : 'bg-surface-4 text-muted-foreground border-border'}`}>
          <Flag size={10} /> PEP: {ubo.isPEP ? 'Declared' : 'None'}
        </span>
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${ubo.sanctionHit ? 'bg-critical/15 text-critical border-critical/30' : 'bg-surface-4 text-muted-foreground border-border'}`}>
          <Shield size={10} /> Sanctions: {ubo.sanctionHit ? 'HIT — OFAC SDN' : 'Clear'}
        </span>
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${ubo.docStatus === 'verified' ? 'bg-positive/15 text-positive border-positive/30' : 'bg-surface-4 text-muted-foreground border-border'}`}>
          <FileText size={10} /> Docs: {ubo.docStatus.charAt(0).toUpperCase() + ubo.docStatus.slice(1)}
        </span>
      </div>

      {/* Reject reason input */}
      {showRejectInput && (
        <div className="space-y-2">
          <textarea
            rows={2}
            placeholder="State reason for UBO rejection (required)…"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="w-full bg-surface-4 border border-negative/40 rounded-md px-3 py-2 text-xs text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:border-negative"
          />
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (rejectReason.trim()) {
                  onDecision(ubo.id, 'rejected', rejectReason);
                  setShowRejectInput(false);
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-negative/20 text-negative border border-negative/40 hover:bg-negative/30 transition-colors"
            >
              <XCircle size={12} /> Confirm Rejection
            </button>
            <button
              onClick={() => setShowRejectInput(false)}
              className="px-3 py-1.5 rounded-md text-xs font-medium bg-surface-4 text-muted-foreground border border-border hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Decision buttons */}
      {!showRejectInput && ubo.decision === 'pending' && (
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={() => onDecision(ubo.id, 'approved')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-positive/15 text-positive border border-positive/30 hover:bg-positive/25 transition-colors"
          >
            <ThumbsUp size={12} /> Approve UBO
          </button>
          <button
            onClick={() => setShowRejectInput(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-negative/15 text-negative border border-negative/30 hover:bg-negative/25 transition-colors"
          >
            <ThumbsDown size={12} /> Reject UBO
          </button>
        </div>
      )}

      {ubo.decision !== 'pending' && (
        <button
          onClick={() => onDecision(ubo.id, 'pending')}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
        >
          <RefreshCw size={10} /> Reset decision
        </button>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function KYCApprovalWorkflow() {
  const [activeSection, setActiveSection] = useState<'documents' | 'liveness' | 'ubo' | 'risk' | 'decision'>('documents');
  const [docs, setDocs] = useState<ApplicantDoc[]>(INITIAL_DOCS);
  const [ubos, setUbos] = useState<UBOEntry[]>(INITIAL_UBOS);
  const [livenessStatus, setLivenessStatus] = useState<LivenessStatus>('passed');
  const [livenessOverrideNote, setLivenessOverrideNote] = useState('');
  const [selectedRiskTier, setSelectedRiskTier] = useState<RiskTier | null>(null);
  const [riskRationale, setRiskRationale] = useState('');
  const [workflowDecision, setWorkflowDecision] = useState<WorkflowDecision>(null);
  const [holdReason, setHoldReason] = useState('');
  const [auditLog, setAuditLog] = useState<AuditEvent[]>(INITIAL_AUDIT);
  const [decisionEmitted, setDecisionEmitted] = useState(false);

  const addAuditEvent = (action: string, detail: string) => {
    setAuditLog((prev) => [
      ...prev,
      { ts: '2026-08-20 16:37', actor: 'L. Okafor', action, detail },
    ]);
  };

  const handleDocStatusChange = (id: string, status: DocStatus) => {
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, status } : d)));
    addAuditEvent('Document Status Updated', `${docs.find((d) => d.id === id)?.type} → ${status}`);
  };

  const handleDocNoteChange = (id: string, note: string) => {
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, notes: note } : d)));
  };

  const handleUBODecision = (id: string, decision: UBODecision, reason?: string) => {
    setUbos((prev) => prev.map((u) => (u.id === id ? { ...u, decision, rejectionReason: reason } : u)));
    const ubo = ubos.find((u) => u.id === id);
    addAuditEvent('UBO Decision', `${ubo?.name} → ${decision}${reason ? ` — Reason: ${reason}` : ''}`);
  };

  const handleEmitDecision = () => {
    if (!workflowDecision || !selectedRiskTier) return;
    addAuditEvent(
      workflowDecision === 'eligible' ? 'Eligibility Event Emitted' : 'Compliance Hold Emitted',
      `Risk tier: ${selectedRiskTier.toUpperCase()}. ${workflowDecision === 'hold' ? `Hold reason: ${holdReason}` : 'Investor cleared for onboarding.'}`
    );
    setDecisionEmitted(true);
  };

  // Derived stats
  const verifiedDocs = docs.filter((d) => d.status === 'verified').length;
  const rejectedDocs = docs.filter((d) => d.status === 'rejected').length;
  const approvedUBOs = ubos.filter((u) => u.decision === 'approved').length;
  const rejectedUBOs = ubos.filter((u) => u.decision === 'rejected').length;
  const pendingUBOs = ubos.filter((u) => u.decision === 'pending').length;
  const sanctionUBOs = ubos.filter((u) => u.sanctionHit).length;

  const sections = [
    { id: 'documents', label: 'Document Review', icon: FileText },
    { id: 'liveness', label: 'Liveness Verification', icon: Camera },
    { id: 'ubo', label: 'UBO Declarations', icon: Users },
    { id: 'risk', label: 'Risk Tier Assignment', icon: BarChart3 },
    { id: 'decision', label: 'Compliance Decision', icon: Send },
  ] as const;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <a href="/compliance-portal" className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
              <ChevronLeft size={12} /> Compliance Portal
            </a>
            <ChevronRight size={12} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">KYC/KYB Approval</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">KYC/KYB Approval Workflow</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {APPLICANT.name} · {APPLICANT.type} · {APPLICANT.queue} · Case {APPLICANT.id}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono-data text-muted-foreground bg-surface-3 border border-border px-2 py-1 rounded-md">
            Assignee: {APPLICANT.assignee}
          </span>
          <button className="btn-secondary text-xs gap-1.5"><Download size={12} /> Export Case</button>
        </div>
      </div>

      {/* Sanctions alert banner */}
      {sanctionUBOs > 0 && (
        <div className="flex items-start gap-3 p-3 rounded-xl border border-critical/40 bg-critical/10">
          <AlertTriangle size={16} className="text-critical flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-critical">Sanctions Hit Detected — Immediate Review Required</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Dmitri Volkov (UBO-1, 62% ownership) has a partial OFAC SDN match (score: 87/100). Case cannot proceed to eligibility without MLRO sign-off.
            </p>
          </div>
          <button className="flex-shrink-0 btn-ghost text-xs text-critical gap-1">
            Escalate <ArrowRight size={11} />
          </button>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Docs Verified" value={`${verifiedDocs}/${docs.length}`} subValue={`${rejectedDocs} rejected`} variant={rejectedDocs > 0 ? 'warning' : 'positive'} icon={FileText} iconColor={rejectedDocs > 0 ? 'text-warning' : 'text-positive'} mono />
        <MetricCard label="Liveness Status" value={livenessStatus === 'passed' ? 'Passed' : livenessStatus === 'failed' ? 'Failed' : livenessStatus === 'manual-override' ? 'Override' : 'Pending'} subValue="eKYC biometric check" variant={livenessStatus === 'passed' || livenessStatus === 'manual-override' ? 'positive' : livenessStatus === 'failed' ? 'negative' : 'default'} icon={Camera} iconColor={livenessStatus === 'passed' ? 'text-positive' : 'text-warning'} />
        <MetricCard label="UBO Decisions" value={`${approvedUBOs + rejectedUBOs}/${ubos.length}`} subValue={`${pendingUBOs} pending · ${sanctionUBOs} sanction hit`} variant={sanctionUBOs > 0 ? 'critical' : pendingUBOs > 0 ? 'warning' : 'positive'} icon={Users} iconColor={sanctionUBOs > 0 ? 'text-critical' : 'text-warning'} />
        <MetricCard label="Risk Tier" value={selectedRiskTier ? selectedRiskTier.charAt(0).toUpperCase() + selectedRiskTier.slice(1) : 'Not Set'} subValue={workflowDecision ? (workflowDecision === 'eligible' ? 'Eligibility emitted' : 'Hold emitted') : 'Awaiting assignment'} variant={selectedRiskTier === 'critical' ? 'critical' : selectedRiskTier === 'high' ? 'negative' : selectedRiskTier === 'medium' ? 'warning' : selectedRiskTier === 'low' ? 'positive' : 'default'} icon={Shield} iconColor="text-primary" />
      </div>

      {/* Applicant summary card */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
              <Building2 size={20} className="text-primary" />
            </div>
            <div>
              <p className="font-bold text-foreground">{APPLICANT.name}</p>
              <p className="text-xs text-muted-foreground">{APPLICANT.type} · {APPLICANT.jurisdiction}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-xs">
            <div><p className="text-muted-foreground">Case ID</p><p className="font-mono-data text-foreground font-medium">{APPLICANT.id}</p></div>
            <div><p className="text-muted-foreground">Intake Ref</p><p className="font-mono-data text-foreground font-medium">{APPLICANT.intakeRef}</p></div>
            <div><p className="text-muted-foreground">FATCA Status</p><p className="text-foreground font-medium">{APPLICANT.fatcaStatus}</p></div>
            <div><p className="text-muted-foreground">PEP Declared</p><p className={`font-medium ${APPLICANT.pepDeclared ? 'text-warning' : 'text-positive'}`}>{APPLICANT.pepDeclared ? 'Yes' : 'No'}</p></div>
            <div><p className="text-muted-foreground">Submitted</p><p className="font-mono-data text-foreground">{APPLICANT.submittedAt}</p></div>
            <div><p className="text-muted-foreground">Queue</p><p className="text-foreground font-medium">{APPLICANT.queue}</p></div>
            <div><p className="text-muted-foreground">Incorporation No.</p><p className="font-mono-data text-foreground">{APPLICANT.incorporationNo}</p></div>
            <div><p className="text-muted-foreground">Email</p><p className="text-foreground truncate">{APPLICANT.email}</p></div>
          </div>
        </div>
      </div>

      {/* Section nav + content */}
      <div className="flex gap-0 border-b border-border overflow-x-auto">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`portal-tab flex items-center gap-1.5 ${activeSection === s.id ? 'active' : ''}`}
          >
            <s.icon size={13} />
            {s.label}
          </button>
        ))}
      </div>

      {/* ── DOCUMENT REVIEW ── */}
      {activeSection === 'documents' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <SectionHeader
              title="Identity & Corporate Document Review"
              subtitle={`${docs.length} documents submitted · ${verifiedDocs} verified · ${rejectedDocs} rejected`}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setDocs((prev) => prev.map((d) => ({ ...d, status: 'verified' as DocStatus })));
                  addAuditEvent('Bulk Verify', 'All documents marked as verified by officer.');
                }}
                className="btn-secondary text-xs gap-1.5"
              >
                <CheckCircle size={12} /> Verify All
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {docs.map((doc) => (
              <DocReviewRow
                key={doc.id}
                doc={doc}
                onStatusChange={handleDocStatusChange}
                onNoteChange={handleDocNoteChange}
              />
            ))}
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Lock size={12} />
              All documents stored in AES-256 encrypted vault. Access logged.
            </div>
            <button
              onClick={() => setActiveSection('liveness')}
              className="btn-primary text-xs gap-1.5"
            >
              Next: Liveness Verification <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* ── LIVENESS VERIFICATION ── */}
      {activeSection === 'liveness' && (
        <div className="space-y-4">
          <SectionHeader
            title="Liveness Verification Status"
            subtitle="eKYC biometric session result and officer override controls"
          />

          {/* Status card */}
          <div className={`rounded-xl border p-5 ${livenessStatus === 'passed' ? 'border-positive/40 bg-positive/5' : livenessStatus === 'failed' ? 'border-negative/40 bg-negative/5' : livenessStatus === 'manual-override' ? 'border-warning/40 bg-warning/5' : 'border-border bg-surface-3'}`}>
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${livenessStatus === 'passed' ? 'bg-positive/20' : livenessStatus === 'failed' ? 'bg-negative/20' : livenessStatus === 'manual-override' ? 'bg-warning/20' : 'bg-surface-4'}`}>
                {livenessStatus === 'passed' && <CheckCircle size={28} className="text-positive" />}
                {livenessStatus === 'failed' && <XCircle size={28} className="text-negative" />}
                {livenessStatus === 'manual-override' && <UserCheck size={28} className="text-warning" />}
                {livenessStatus === 'not-started' && <Camera size={28} className="text-muted-foreground" />}
              </div>
              <div className="flex-1">
                <p className="font-bold text-foreground text-lg">
                  {livenessStatus === 'passed' && 'Liveness Check Passed'}
                  {livenessStatus === 'failed' && 'Liveness Check Failed'}
                  {livenessStatus === 'manual-override' && 'Manual Override Applied'}
                  {livenessStatus === 'not-started' && 'Liveness Check Not Started'}
                </p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {livenessStatus === 'passed' && 'Biometric match confidence: 98.4% · No spoof detected · Session ID: LV-882-4491'}
                  {livenessStatus === 'failed' && 'Biometric match below threshold (< 70%). Manual review required.'}
                  {livenessStatus === 'manual-override' && `Officer override applied. ${livenessOverrideNote || 'No note provided.'}`}
                  {livenessStatus === 'not-started' && 'eKYC session has not been initiated for this applicant.'}
                </p>
              </div>
            </div>
          </div>

          {/* Session log */}
          <div className="glass-card rounded-xl p-4 space-y-3">
            <SectionHeader title="eKYC Session Log" subtitle="Provider: Onfido · Session LV-882-4491" />
            <div className="space-y-2">
              {[
                { ts: '2026-08-18 10:02', event: 'Session Initiated', detail: 'eKYC session launched. Applicant notified via email.', ok: true },
                { ts: '2026-08-18 10:04', event: 'Document Capture', detail: 'Passport captured. MRZ extracted successfully.', ok: true },
                { ts: '2026-08-18 10:06', event: 'Facial Biometric', detail: 'Selfie captured. Liveness challenge completed.', ok: true },
                { ts: '2026-08-18 10:09', event: 'Result Received', detail: 'Match confidence: 98.4%. Spoof score: 0.02. PASS.', ok: true },
              ].map((entry, i) => (
                <div key={`lv-log-${i}`} className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${entry.ok ? 'bg-positive/20' : 'bg-negative/20'}`}>
                    {entry.ok ? <CheckCircle size={11} className="text-positive" /> : <XCircle size={11} className="text-negative" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-foreground">{entry.event}</p>
                      <span className="text-xs font-mono-data text-muted-foreground">{entry.ts}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{entry.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Officer override panel */}
          <div className="glass-card rounded-xl p-4 space-y-3">
            <SectionHeader title="Officer Override Controls" subtitle="Override liveness result with documented justification" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {([
                { status: 'passed' as LivenessStatus, label: 'Mark as Passed', icon: CheckCircle, color: 'positive' },
                { status: 'failed' as LivenessStatus, label: 'Mark as Failed', icon: XCircle, color: 'negative' },
                { status: 'manual-override' as LivenessStatus, label: 'Manual Override', icon: UserCheck, color: 'warning' },
              ] as const).map((opt) => (
                <button
                  key={opt.status}
                  onClick={() => {
                    setLivenessStatus(opt.status);
                    addAuditEvent('Liveness Override', `Status set to: ${opt.status}`);
                  }}
                  className={`flex items-center gap-2 p-3 rounded-lg border text-sm font-medium transition-colors ${livenessStatus === opt.status ? `bg-${opt.color}/20 text-${opt.color} border-${opt.color}/40` : 'bg-surface-3 text-muted-foreground border-border hover:text-foreground'}`}
                >
                  <opt.icon size={15} />
                  {opt.label}
                </button>
              ))}
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-medium block mb-1.5">Override Justification (required for manual override)</label>
              <textarea
                rows={2}
                placeholder="Document reason for override (e.g., applicant unable to complete eKYC due to technical issue — verified via video call)…"
                value={livenessOverrideNote}
                onChange={(e) => setLivenessOverrideNote(e.target.value)}
                className="w-full bg-surface-4 border border-border rounded-md px-3 py-2 text-xs text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setActiveSection('documents')} className="btn-secondary text-xs gap-1.5">
              <ChevronLeft size={12} /> Back
            </button>
            <button onClick={() => setActiveSection('ubo')} className="btn-primary text-xs gap-1.5">
              Next: UBO Declarations <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* ── UBO DECLARATIONS ── */}
      {activeSection === 'ubo' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <SectionHeader
              title="UBO Declaration Review"
              subtitle={`${ubos.length} declared UBOs · ${approvedUBOs} approved · ${rejectedUBOs} rejected · ${pendingUBOs} pending`}
            />
          </div>

          {ubos.some((u) => u.sanctionHit) && (
            <div className="flex items-start gap-3 p-3 rounded-xl border border-critical/40 bg-critical/10">
              <AlertTriangle size={14} className="text-critical flex-shrink-0 mt-0.5" />
              <p className="text-xs text-critical">
                <span className="font-semibold">Sanctions screening alert:</span> One or more UBOs have active sanctions hits. Approval of these UBOs requires MLRO escalation and cannot be completed by a standard compliance officer.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {ubos.map((ubo) => (
              <UBOReviewCard key={ubo.id} ubo={ubo} onDecision={handleUBODecision} />
            ))}
          </div>

          {/* Aggregate ownership check */}
          <div className="glass-card rounded-xl p-4">
            <SectionHeader title="Aggregate Ownership Verification" subtitle="Total declared ownership must equal 100%" />
            <div className="flex items-center gap-4 mt-3">
              <div className="flex-1 h-3 rounded-full bg-surface-4 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary to-cyan-400 transition-all"
                  style={{ width: `${Math.min(ubos.reduce((s, u) => s + u.ownership, 0), 100)}%` }}
                />
              </div>
              <span className="font-mono-data text-sm font-bold text-foreground flex-shrink-0">
                {ubos.reduce((s, u) => s + u.ownership, 0)}% / 100%
              </span>
              {ubos.reduce((s, u) => s + u.ownership, 0) === 100 ? (
                <CheckCircle size={16} className="text-positive flex-shrink-0" />
              ) : (
                <AlertTriangle size={16} className="text-warning flex-shrink-0" />
              )}
            </div>
            {ubos.reduce((s, u) => s + u.ownership, 0) !== 100 && (
              <p className="text-xs text-warning mt-2 flex items-center gap-1">
                <Info size={11} /> Declared ownership does not sum to 100%. Remaining {100 - ubos.reduce((s, u) => s + u.ownership, 0)}% may indicate undisclosed beneficial owners.
              </p>
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setActiveSection('liveness')} className="btn-secondary text-xs gap-1.5">
              <ChevronLeft size={12} /> Back
            </button>
            <button onClick={() => setActiveSection('risk')} className="btn-primary text-xs gap-1.5">
              Next: Risk Tier Assignment <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* ── RISK TIER ASSIGNMENT ── */}
      {activeSection === 'risk' && (
        <div className="space-y-4">
          <SectionHeader
            title="Risk Tier Assignment"
            subtitle="Assign a risk classification based on document review, liveness, UBO findings, and AML screening"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {RISK_TIERS.map((rt) => (
              <button
                key={rt.tier}
                onClick={() => {
                  setSelectedRiskTier(rt.tier);
                  addAuditEvent('Risk Tier Assigned', `Tier set to: ${rt.tier.toUpperCase()}`);
                }}
                className={`text-left p-4 rounded-xl border transition-all ${selectedRiskTier === rt.tier ? `border-current bg-surface-3 ring-1 ring-current ${rt.color}` : 'border-border bg-surface-3 hover:border-border/80'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className={`font-bold text-sm ${selectedRiskTier === rt.tier ? rt.color : 'text-foreground'}`}>{rt.label}</p>
                  {selectedRiskTier === rt.tier && <CheckCircle size={16} className={rt.color} />}
                </div>
                <p className="text-xs text-muted-foreground">{rt.description}</p>
              </button>
            ))}
          </div>

          {/* Risk factors summary */}
          <div className="glass-card rounded-xl p-4 space-y-3">
            <SectionHeader title="Risk Factor Summary" subtitle="Automated risk signals detected during review" />
            <div className="space-y-2">
              {[
                { factor: 'Sanctions Hit (UBO-1)', severity: 'critical', detail: 'Dmitri Volkov — OFAC SDN partial match, score 87/100' },
                { factor: 'PEP Declared (UBO-2)', severity: 'high', detail: 'Amelia Hartmann — Tier 1 PEP, German political exposure' },
                { factor: 'High-Risk Jurisdiction', severity: 'high', detail: 'Cayman Islands — FATF grey-list monitoring' },
                { factor: 'Passive NFFE (FATCA)', severity: 'medium', detail: 'Enhanced FATCA reporting obligations apply' },
                { factor: 'Undisclosed Ownership', severity: 'medium', detail: 'Thornfield Trust (10%) — nominee structure, beneficial owner unclear' },
                { factor: 'Document Rejected', severity: 'medium', detail: 'UBO passport flagged — possible OFAC match' },
              ].map((rf, i) => (
                <div key={`rf-${i}`} className="flex items-start gap-3 p-2.5 rounded-lg bg-surface-3 border border-border">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border flex-shrink-0 mt-0.5 ${rf.severity === 'critical' ? 'bg-critical/15 text-critical border-critical/30' : rf.severity === 'high' ? 'bg-negative/15 text-negative border-negative/30' : 'bg-warning/15 text-warning border-warning/30'}`}>
                    {rf.severity}
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-foreground">{rf.factor}</p>
                    <p className="text-xs text-muted-foreground">{rf.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rationale */}
          <div>
            <label className="text-xs text-muted-foreground font-medium block mb-1.5">Risk Tier Rationale (required)</label>
            <textarea
              rows={3}
              placeholder="Document the basis for the assigned risk tier, referencing specific findings from document review, liveness check, UBO screening, and AML signals…"
              value={riskRationale}
              onChange={(e) => setRiskRationale(e.target.value)}
              className="w-full bg-surface-4 border border-border rounded-md px-3 py-2 text-xs text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:border-primary"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setActiveSection('ubo')} className="btn-secondary text-xs gap-1.5">
              <ChevronLeft size={12} /> Back
            </button>
            <button
              onClick={() => setActiveSection('decision')}
              disabled={!selectedRiskTier}
              className="btn-primary text-xs gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Compliance Decision <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* ── COMPLIANCE DECISION ── */}
      {activeSection === 'decision' && (
        <div className="space-y-4">
          <SectionHeader
            title="Compliance Decision — Emit Event"
            subtitle="Issue a compliance hold or eligibility clearance event for this applicant"
          />

          {decisionEmitted ? (
            <div className="rounded-xl border border-positive/40 bg-positive/5 p-6 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-positive/20 flex items-center justify-center mx-auto">
                <CheckCircle size={28} className="text-positive" />
              </div>
              <p className="font-bold text-foreground text-lg">
                {workflowDecision === 'eligible' ? 'Eligibility Event Emitted' : 'Compliance Hold Emitted'}
              </p>
              <p className="text-sm text-muted-foreground">
                {workflowDecision === 'eligible'
                  ? `${APPLICANT.name} has been cleared for onboarding. Risk tier: ${selectedRiskTier?.toUpperCase()}. Investor account activation event dispatched.`
                  : `Compliance hold placed on ${APPLICANT.name}. Risk tier: ${selectedRiskTier?.toUpperCase()}. Account activation blocked pending resolution.`}
              </p>
              <div className="flex items-center justify-center gap-2 text-xs font-mono-data text-muted-foreground">
                <Clock size={12} />
                Event emitted: 2026-08-20 16:37 UTC · By: L. Okafor
              </div>
            </div>
          ) : (
            <>
              {/* Decision selector */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => setWorkflowDecision('eligible')}
                  className={`p-5 rounded-xl border text-left transition-all ${workflowDecision === 'eligible' ? 'border-positive/50 bg-positive/10 ring-1 ring-positive/40' : 'border-border bg-surface-3 hover:border-positive/30'}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-positive/20 flex items-center justify-center">
                      <Zap size={18} className="text-positive" />
                    </div>
                    <p className="font-bold text-foreground">Emit Eligibility Event</p>
                    {workflowDecision === 'eligible' && <CheckCircle size={16} className="text-positive ml-auto" />}
                  </div>
                  <p className="text-xs text-muted-foreground">Applicant passes compliance review. Investor account activation event dispatched to onboarding pipeline.</p>
                </button>

                <button
                  onClick={() => setWorkflowDecision('hold')}
                  className={`p-5 rounded-xl border text-left transition-all ${workflowDecision === 'hold' ? 'border-negative/50 bg-negative/10 ring-1 ring-negative/40' : 'border-border bg-surface-3 hover:border-negative/30'}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-negative/20 flex items-center justify-center">
                      <Lock size={18} className="text-negative" />
                    </div>
                    <p className="font-bold text-foreground">Emit Compliance Hold</p>
                    {workflowDecision === 'hold' && <CheckCircle size={16} className="text-negative ml-auto" />}
                  </div>
                  <p className="text-xs text-muted-foreground">Place a compliance hold on the applicant. Account activation blocked. Reason documented in audit trail.</p>
                </button>
              </div>

              {/* Hold reason */}
              {workflowDecision === 'hold' && (
                <div>
                  <label className="text-xs text-muted-foreground font-medium block mb-1.5">Hold Reason (required)</label>
                  <textarea
                    rows={3}
                    placeholder="State the specific compliance grounds for placing this hold (e.g., unresolved sanctions hit on UBO-1, pending MLRO escalation)…"
                    value={holdReason}
                    onChange={(e) => setHoldReason(e.target.value)}
                    className="w-full bg-surface-4 border border-border rounded-md px-3 py-2 text-xs text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:border-primary"
                  />
                </div>
              )}

              {/* Decision summary */}
              {workflowDecision && selectedRiskTier && (
                <div className="glass-card rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Decision Summary</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div><p className="text-muted-foreground">Applicant</p><p className="text-foreground font-medium truncate">{APPLICANT.name}</p></div>
                    <div><p className="text-muted-foreground">Decision</p><p className={`font-bold ${workflowDecision === 'eligible' ? 'text-positive' : 'text-negative'}`}>{workflowDecision === 'eligible' ? 'ELIGIBLE' : 'HOLD'}</p></div>
                    <div><p className="text-muted-foreground">Risk Tier</p><p className={`font-bold ${selectedRiskTier === 'critical' ? 'text-critical' : selectedRiskTier === 'high' ? 'text-negative' : selectedRiskTier === 'medium' ? 'text-warning' : 'text-positive'}`}>{selectedRiskTier.toUpperCase()}</p></div>
                    <div><p className="text-muted-foreground">Officer</p><p className="text-foreground font-medium">{APPLICANT.assignee}</p></div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <button onClick={() => setActiveSection('risk')} className="btn-secondary text-xs gap-1.5">
                  <ChevronLeft size={12} /> Back
                </button>
                <button
                  onClick={handleEmitDecision}
                  disabled={!workflowDecision || !selectedRiskTier || (workflowDecision === 'hold' && !holdReason.trim())}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${workflowDecision === 'hold' ? 'bg-negative text-white hover:bg-negative/90' : 'btn-primary'}`}
                >
                  <Send size={13} />
                  {workflowDecision === 'hold' ? 'Emit Compliance Hold' : 'Emit Eligibility Event'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── AUDIT TRAIL ── */}
      <div className="glass-card rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <SectionHeader title="Case Audit Trail" subtitle={`${auditLog.length} events · Immutable log`} />
          <button className="btn-ghost text-xs gap-1.5"><Download size={12} /> Export</button>
        </div>
        <div className="space-y-0 divide-y divide-border">
          {[...auditLog].reverse().map((event, i) => (
            <div key={`audit-${i}`} className="flex items-start gap-3 py-2.5">
              <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-xs font-semibold text-foreground">{event.action}</p>
                  <span className="text-xs text-muted-foreground">by {event.actor}</span>
                  <span className="text-xs font-mono-data text-muted-foreground ml-auto">{event.ts}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{event.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
